import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

function Logo({ className = "h-9 w-auto" }) {
  return (
    <Link to="/" className="flex items-center gap-2">
      <img src="/logo.png" alt="NCAI" className={className} />
      <span className="text-xl font-extrabold tracking-tight">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">NCAI</span>
      </span>
    </Link>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-slate-200/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/#programs" className="hover:text-indigo-600">Programs</Link>
            <Link to="/workshops" className="hover:text-indigo-600">Workshops</Link>
            <Link to="/#flex" className="hover:text-indigo-600">Flexible Learning</Link>
            <Link to="/#outcomes" className="hover:text-indigo-600">Outcomes</Link>
            <Link to="/#faq" className="hover:text-indigo-600">FAQ</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/apply"
              className="rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 shadow-md hover:shadow-lg transition"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8 lg:grid-cols-4 md:grid-cols-2">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <Logo />
              <span className="text-sm font-semibold text-slate-700">NeuraCore AI Institute</span>
            </Link>
            <p className="text-sm text-slate-600 mb-4">
              Empowering careers in Digital Marketing, AI & Machine Learning, and Hardware Design & Manufacturing.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.749.097.118.112.221.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.747-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001.012.017z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/#programs" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Programs</Link></li>
              <li><Link to="/workshops" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Workshops</Link></li>
              <li><Link to="/#flex" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Flexible Learning</Link></li>
              <li><Link to="/#outcomes" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">Outcomes</Link></li>
              <li><Link to="/#faq" className="text-sm text-slate-600 hover:text-indigo-600 transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Us</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:info@ncai.edu" className="hover:text-indigo-600 transition-colors">info@ncai.edu</a>
              </li>
              <li className="flex items-center gap-2">
                <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+917356011039" className="hover:text-indigo-600 transition-colors">7356011039</a>
              </li>
              <li className="flex items-start gap-2">
                <svg className="h-4 w-4 text-slate-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Opposite to John Cox Engineer College,<br />Kannanmoola, Trivandrum</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Stay Updated</h3>
            <p className="text-sm text-slate-600 mb-4">Subscribe to our newsletter for the latest updates and insights.</p>
            <form className="space-y-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="submit"
                className="w-full px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 rounded-lg hover:shadow-md transition"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">© {new Date().getFullYear()} NeuraCore AI Institute (NCAI). All rights reserved.</p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-indigo-600 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-indigo-600 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

const steps = [
  { id: 1, title: "Program Selection", description: "Choose your preferred program" },
  { id: 2, title: "Personal Information", description: "Tell us about yourself" },
  { id: 3, title: "Educational & Professional Background", description: "Share your background" },
  { id: 4, title: "Goals & Expectations", description: "What do you hope to achieve?" },
  { id: 5, title: "Review & Submit", description: "Review your application" }
];

export default function ApplicationForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    program: "",
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: ""
    },
    education: {
      highestEducation: "",
      institution: "",
      graduationYear: "",
      gpa: "",
      fieldOfStudy: ""
    },
    professional: {
      currentEmployment: "",
      jobTitle: "",
      company: "",
      experience: "",
      skills: "",
      linkedin: "",
      portfolio: ""
    },
    goals: {
      primaryGoal: "",
      expectedOutcome: "",
      timeCommitment: "",
      previousExperience: "",
      motivation: "",
      challenges: ""
    }
  });

  // Load saved data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem("applicationForm");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed.formData);
        setCurrentStep(parsed.currentStep);
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    }
  }, []);

  // Save data to localStorage whenever formData or currentStep changes
  useEffect(() => {
    localStorage.setItem("applicationForm", JSON.stringify({
      formData,
      currentStep
    }));
  }, [formData, currentStep]);

  const updateFormData = (section, data) => {
    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      // Direct property update
      setFormData(prev => ({
        ...prev,
        [section]: data
      }));
    } else {
      // Nested object update
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], ...data }
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepId) => {
    setCurrentStep(stepId);
  };

  const handleSubmit = () => {
    // Handle form submission
    console.log("Submitting application:", formData);
    // Clear localStorage after successful submission
    localStorage.removeItem("applicationForm");
    alert("Application submitted successfully!");
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ProgramSelectionStep formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <PersonalInfoStep formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <EducationStep formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <GoalsStep formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <ReviewStep formData={formData} onSubmit={handleSubmit} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <SiteHeader />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Application Form</h1>
          <p className="mt-2 text-slate-600 text-sm sm:text-base">Join NeuraCore AI Institute and start your career transformation</p>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator steps={steps} currentStep={currentStep} goToStep={goToStep} />

        {/* Form Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between gap-4">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-4 sm:px-6 py-3 text-slate-600 border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base order-2 sm:order-1"
          >
            Previous
          </button>
          {currentStep < steps.length ? (
            <button
              onClick={nextStep}
              className="px-4 sm:px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl hover:shadow-lg text-sm sm:text-base order-1 sm:order-2"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg text-sm sm:text-base order-1 sm:order-2"
            >
              Submit Application
            </button>
          )}
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

function ProgressIndicator({ steps, currentStep, goToStep }) {
   return (
     <div className="mb-6 sm:mb-8">
       <div className="flex items-center justify-between">
         {steps.map((step, index) => (
           <div key={step.id} className="flex items-center flex-1">
             <button
               onClick={() => goToStep(step.id)}
               className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-colors text-xs sm:text-sm ${
                 step.id <= currentStep
                   ? "bg-indigo-600 border-indigo-600 text-white"
                   : "border-slate-300 text-slate-400 hover:border-slate-400"
               }`}
             >
               {step.id < currentStep ? (
                 <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                   <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                 </svg>
               ) : (
                 step.id
               )}
             </button>
             <div className="ml-2 sm:ml-4 hidden sm:block flex-1">
               <div className={`text-xs sm:text-sm font-semibold ${step.id <= currentStep ? "text-slate-900" : "text-slate-400"}`}>
                 {step.title}
               </div>
               <div className="text-xs text-slate-500">{step.description}</div>
             </div>
             {index < steps.length - 1 && (
               <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step.id < currentStep ? "bg-indigo-600" : "bg-slate-200"}`} />
             )}
           </div>
         ))}
       </div>
       {/* Mobile step labels */}
       <div className="flex justify-between mt-2 sm:hidden">
         {steps.map((step) => (
           <div key={step.id} className="text-center">
             <div className={`text-xs font-semibold ${step.id <= currentStep ? "text-slate-900" : "text-slate-400"}`}>
               {step.title}
             </div>
           </div>
         ))}
       </div>
     </div>
   );
 }

// Step Components will be implemented next
function ProgramSelectionStep({ formData, updateFormData }) {
  const programs = [
    { id: "digital-marketing", name: "Digital Marketing", description: "Master SEO, Google Ads, Social Media, and Analytics" },
    { id: "ai-ml", name: "AI & Machine Learning", description: "Learn Python, ML algorithms, Deep Learning, and AI applications" },
    { id: "hardware", name: "Hardware Design & Manufacturing", description: "PCB design, 3D modeling, fabrication, and quality control" },
    { id: "industrial-design", name: "Industrial Design and Manufacturing", description: "Product design principles, prototyping, manufacturing processes, and quality control" },
    { id: "smt-assembly", name: "SMT Assembly Operator", description: "Basics of electronics, SMT process, safety guidelines, and soft skills" }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Choose Your Program</h2>
      <div className="space-y-4">
        {programs.map((program) => (
          <label key={program.id} className="block">
            <input
              type="radio"
              name="program"
              value={program.id}
              checked={formData.program === program.id}
              onChange={(e) => updateFormData("program", e.target.value)}
              className="sr-only"
            />
            <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
              formData.program === program.id
                ? "border-indigo-600 bg-indigo-50 shadow-md"
                : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 hover:shadow-sm hover:scale-[1.02]"
            }`}>
              <div className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                  formData.program === program.id ? "border-indigo-600 bg-indigo-600" : "border-slate-300"
                }`}>
                  {formData.program === program.id && (
                    <div className="w-full h-full rounded-full bg-white scale-50" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{program.name}</h3>
                  <p className="text-slate-600 text-sm mt-1">{program.description}</p>
                </div>
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function PersonalInfoStep({ formData, updateFormData }) {
  const handleChange = (field, value) => {
    updateFormData("personalInfo", { [field]: value });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Personal Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">First Name *</label>
          <input
            type="text"
            value={formData.personalInfo.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Last Name *</label>
          <input
            type="text"
            value={formData.personalInfo.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
          <input
            type="email"
            value={formData.personalInfo.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
          <input
            type="tel"
            value={formData.personalInfo.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Date of Birth</label>
          <input
            type="date"
            value={formData.personalInfo.dateOfBirth}
            onChange={(e) => handleChange("dateOfBirth", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
          <input
            type="text"
            value={formData.personalInfo.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">City</label>
          <input
            type="text"
            value={formData.personalInfo.city}
            onChange={(e) => handleChange("city", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">State/Province</label>
          <input
            type="text"
            value={formData.personalInfo.state}
            onChange={(e) => handleChange("state", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">ZIP/Postal Code</label>
          <input
            type="text"
            value={formData.personalInfo.zipCode}
            onChange={(e) => handleChange("zipCode", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Country</label>
          <input
            type="text"
            value={formData.personalInfo.country}
            onChange={(e) => handleChange("country", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
    </div>
  );
}

function EducationStep({ formData, updateFormData }) {
  const handleChange = (field, value) => {
    updateFormData("education", { [field]: value });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Educational Background</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Highest Level of Education *</label>
          <select
            value={formData.education.highestEducation}
            onChange={(e) => handleChange("highestEducation", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          >
            <option value="">Select education level</option>
            <option value="high-school">High School</option>
            <option value="associate">Associate Degree</option>
            <option value="bachelor">Bachelor's Degree</option>
            <option value="master">Master's Degree</option>
            <option value="phd">PhD</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Institution Name</label>
          <input
            type="text"
            value={formData.education.institution}
            onChange={(e) => handleChange("institution", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Graduation Year</label>
            <input
              type="number"
              value={formData.education.graduationYear}
              onChange={(e) => handleChange("graduationYear", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              min="1950"
              max={new Date().getFullYear() + 10}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">GPA (if applicable)</label>
            <input
              type="text"
              value={formData.education.gpa}
              onChange={(e) => handleChange("gpa", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., 3.5/4.0"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Field of Study</label>
          <input
            type="text"
            value={formData.education.fieldOfStudy}
            onChange={(e) => handleChange("fieldOfStudy", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6 mt-12">Professional Background</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Current Employment Status</label>
          <select
            value={formData.professional.currentEmployment}
            onChange={(e) => updateFormData("professional", { currentEmployment: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select status</option>
            <option value="employed">Employed</option>
            <option value="self-employed">Self-employed</option>
            <option value="unemployed">Unemployed</option>
            <option value="student">Student</option>
            <option value="retired">Retired</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Job Title</label>
            <input
              type="text"
              value={formData.professional.jobTitle}
              onChange={(e) => updateFormData("professional", { jobTitle: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Company</label>
            <input
              type="text"
              value={formData.professional.company}
              onChange={(e) => updateFormData("professional", { company: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Years of Experience</label>
          <select
            value={formData.professional.experience}
            onChange={(e) => updateFormData("professional", { experience: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select experience</option>
            <option value="0-1">0-1 years</option>
            <option value="1-3">1-3 years</option>
            <option value="3-5">3-5 years</option>
            <option value="5-10">5-10 years</option>
            <option value="10+">10+ years</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Relevant Skills</label>
          <textarea
            value={formData.professional.skills}
            onChange={(e) => updateFormData("professional", { skills: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="3"
            placeholder="List your relevant skills, technologies, or tools"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">LinkedIn Profile</label>
            <input
              type="url"
              value={formData.professional.linkedin}
              onChange={(e) => updateFormData("professional", { linkedin: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Portfolio/Website</label>
            <input
              type="url"
              value={formData.professional.portfolio}
              onChange={(e) => updateFormData("professional", { portfolio: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="https://yourportfolio.com"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function GoalsStep({ formData, updateFormData }) {
  const handleChange = (field, value) => {
    updateFormData("goals", { [field]: value });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Goals & Expectations</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Primary Goal *</label>
          <select
            value={formData.goals.primaryGoal}
            onChange={(e) => handleChange("primaryGoal", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          >
            <option value="">Select your primary goal</option>
            <option value="career-change">Career Change</option>
            <option value="career-advancement">Career Advancement</option>
            <option value="skill-development">Skill Development</option>
            <option value="entrepreneurship">Entrepreneurship</option>
            <option value="personal-interest">Personal Interest</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Expected Outcome</label>
          <textarea
            value={formData.goals.expectedOutcome}
            onChange={(e) => handleChange("expectedOutcome", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="3"
            placeholder="What do you hope to achieve by completing this program?"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Time Commitment</label>
          <select
            value={formData.goals.timeCommitment}
            onChange={(e) => handleChange("timeCommitment", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select time commitment</option>
            <option value="full-time">Full-time (40+ hours/week)</option>
            <option value="part-time">Part-time (20-30 hours/week)</option>
            <option value="flexible">Flexible schedule</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Previous Experience</label>
          <textarea
            value={formData.goals.previousExperience}
            onChange={(e) => handleChange("previousExperience", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="3"
            placeholder="Do you have any previous experience in this field? If so, please describe."
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Motivation</label>
          <textarea
            value={formData.goals.motivation}
            onChange={(e) => handleChange("motivation", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="3"
            placeholder="What motivates you to pursue this program?"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Potential Challenges</label>
          <textarea
            value={formData.goals.challenges}
            onChange={(e) => handleChange("challenges", e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="3"
            placeholder="Are there any challenges you anticipate facing during the program?"
          />
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ formData, onSubmit }) {
  const getProgramName = (programId) => {
    const programs = {
      "digital-marketing": "Digital Marketing",
      "ai-ml": "AI & Machine Learning",
      "hardware": "Hardware Design & Manufacturing",
      "industrial-design": "Industrial Design and Manufacturing",
      "smt-assembly": "SMT Assembly Operator"
    };
    return programs[programId] || programId;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Review Your Application</h2>
      <div className="space-y-8">
        {/* Program Selection */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Program Selection</h3>
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-slate-700"><strong>Selected Program:</strong> {getProgramName(formData.program)}</p>
          </div>
        </div>

        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Personal Information</h3>
          <div className="bg-slate-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><strong>Name:</strong> {formData.personalInfo.firstName} {formData.personalInfo.lastName}</p>
            <p><strong>Email:</strong> {formData.personalInfo.email}</p>
            <p><strong>Phone:</strong> {formData.personalInfo.phone}</p>
            <p><strong>Date of Birth:</strong> {formData.personalInfo.dateOfBirth || "Not provided"}</p>
            <p className="md:col-span-2"><strong>Address:</strong> {formData.personalInfo.address}, {formData.personalInfo.city}, {formData.personalInfo.state} {formData.personalInfo.zipCode}, {formData.personalInfo.country}</p>
          </div>
        </div>

        {/* Education & Professional */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Education & Professional Background</h3>
          <div className="bg-slate-50 p-4 rounded-lg space-y-2">
            <p><strong>Education:</strong> {formData.education.highestEducation} from {formData.education.institution || "Not specified"} ({formData.education.graduationYear || "Year not specified"})</p>
            <p><strong>Field of Study:</strong> {formData.education.fieldOfStudy || "Not specified"}</p>
            <p><strong>Employment:</strong> {formData.professional.currentEmployment || "Not specified"} - {formData.professional.jobTitle || "No title"} at {formData.professional.company || "No company"}</p>
            <p><strong>Experience:</strong> {formData.professional.experience || "Not specified"}</p>
            <p><strong>Skills:</strong> {formData.professional.skills || "Not specified"}</p>
          </div>
        </div>

        {/* Goals & Expectations */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Goals & Expectations</h3>
          <div className="bg-slate-50 p-4 rounded-lg space-y-2">
            <p><strong>Primary Goal:</strong> {formData.goals.primaryGoal}</p>
            <p><strong>Expected Outcome:</strong> {formData.goals.expectedOutcome || "Not specified"}</p>
            <p><strong>Time Commitment:</strong> {formData.goals.timeCommitment || "Not specified"}</p>
            <p><strong>Motivation:</strong> {formData.goals.motivation || "Not specified"}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          By submitting this application, you agree to our terms and conditions. We'll review your application and contact you within 3-5 business days.
        </p>
      </div>
    </div>
  );
}